import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';
import FreshnessBadge from '../../components/FreshnessBadge';
import ModuleShell from '../../components/ModuleShell';
import {
  ADMIN_JOB_STATUS,
  ADMIN_MRR_SERIES,
  ADMIN_MRR_SUMMARY,
  ADMIN_PAYMENT_FAIL_SUMMARY,
  ADMIN_PAYMENT_FAILS,
  ADMIN_REFRESH_STRIPE_CACHE,
} from '../../graphql/adminQueries';
import TimeSeriesChart from '../../components/TimeSeriesChart';
import HorizontalBarChart from '../../components/HorizontalBarChart';
import { adminEnvLabel } from '../../utils/envCopy';
import { formatDateTime, formatCurrency, formatNumber } from '../../utils/format';
import { STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION } from '../../metrics/customerMetricsIntegrity';
import { RangeSelect, useDateRange } from '../home/DateRangeContext';

export const FAIL_LIST_DAILY_CAP = 50;
export const FAIL_LIST_CAP_CAPTION =
  'Roster is truncated at 50 distinct users per day. Use the aggregate count and Stripe for full triage.';

export function uniqueFailUserIds(rows) {
  return new Set((rows || []).map((row) => row.userId).filter(Boolean));
}

export function failListCapHit(rows, cap = FAIL_LIST_DAILY_CAP) {
  return uniqueFailUserIds(rows).size >= cap;
}

export default function RevenueModule() {
  const { isAdmin: canMutate } = useAuth();
  const { range } = useDateRange();
  const [refreshMessage, setRefreshMessage] = useState(null);

  const {
    data: mrrData,
    loading,
    error,
    refetch: refetchMrr,
  } = useQuery(ADMIN_MRR_SUMMARY, {
    variables: { range },
  });

  const { data: seriesData, refetch: refetchSeries } = useQuery(ADMIN_MRR_SERIES, {
    variables: { range },
  });

  const { data: freshnessData, refetch: refetchFreshness } = useQuery(ADMIN_JOB_STATUS);

  const { data: failSummaryData, error: failSummaryError } = useQuery(ADMIN_PAYMENT_FAIL_SUMMARY, {
    variables: { range },
  });

  const { data: failListData, error: failListError } = useQuery(ADMIN_PAYMENT_FAILS, {
    variables: { range },
    skip: !canMutate,
  });

  const [refreshStripeCache, { loading: refreshLoading }] = useMutation(ADMIN_REFRESH_STRIPE_CACHE, {
    onCompleted: async (data) => {
      const result = data?.adminRefreshStripeCache;
      if (result?.success) {
        setRefreshMessage(`Stripe cache refreshed · MRR ${formatCurrency(result.mrrCents)}`);
        await Promise.all([refetchMrr(), refetchSeries(), refetchFreshness()]);
      } else {
        setRefreshMessage('Refresh completed but cache may still be stale.');
      }
    },
    onError: (mutationError) => {
      setRefreshMessage(mutationError.message || 'Stripe cache refresh failed.');
    },
  });

  const mrr = mrrData?.adminMrrSummary;
  const mrrSeries = seriesData?.adminMrrSeries || [];
  const freshness = freshnessData?.adminJobStatus;
  const planRows = [...(mrr?.planBreakdown || [])].sort(
    (a, b) => (b.count ?? 0) - (a.count ?? 0)
  );
  const failCount = failSummaryData?.adminPaymentFailSummary?.count;
  const failResult = failListData?.adminPaymentFails;
  const failRows = failResult?.rows || [];
  const capHit = canMutate && (failResult?.capped === true || failListCapHit(failRows));

  const arpuCents =
    mrr?.activeSubscriptions > 0
      ? Math.round(mrr.mrrCents / mrr.activeSubscriptions)
      : null;

  const hasMrrData = mrr != null;

  const handleRefresh = () => {
    setRefreshMessage(null);
    refreshStripeCache();
  };

  return (
    <ModuleShell
      title="Revenue"
      description={
        <>
          <p>MRR snapshot and plan mix from the Stripe cache. {STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION}</p>
          {freshness ? (
            <div className="freshness-row">
              <FreshnessBadge
                label="Stripe cache"
                asOf={freshness.stripeCacheAsOf}
                status={freshness.stripeCacheStatus}
              />
            </div>
          ) : null}
        </>
      }
      actions={
        <>
          <RangeSelect id="revenue-range" />
          {canMutate && (
            <button
              type="button"
              className="secondary-button"
              onClick={handleRefresh}
              disabled={refreshLoading}
            >
              {refreshLoading ? 'Refreshing…' : 'Refresh Stripe cache'}
            </button>
          )}
        </>
      }
      loading={loading}
      loadingLabel="Loading revenue metrics…"
      error={error}
      errorLabel={`Could not load revenue data. Confirm ${adminEnvLabel()} API admin queries are deployed.`}
    >
      {refreshMessage && <p className="module-status">{refreshMessage}</p>}

      {!loading && !error && !hasMrrData && (
        <p className="module-status">
          No subscription data in cache. Stripe cache refresh may be pending on {adminEnvLabel()}.
        </p>
      )}

      <div className="kpi-grid">
        {hasMrrData ? (
          <>
            <article className="kpi-card">
              <p className="kpi-label">MRR</p>
              <p className="kpi-value">{formatCurrency(mrr.mrrCents, mrr.currency)}</p>
              <p className="kpi-hint">
                {mrr.asOf ? `Stripe cache ${formatDateTime(mrr.asOf)}` : 'Stripe cache pending'}
              </p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Paying accounts</p>
              <p className="kpi-value">{formatNumber(mrr.activeSubscriptions)}</p>
              <p className="kpi-hint">{STRIPE_PAID_SUBSCRIPTION_INTEGRITY_CAPTION}</p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">ARPU</p>
              <p className="kpi-value">{formatCurrency(arpuCents, mrr.currency)}</p>
              <p className="kpi-hint">MRR ÷ paid subs</p>
            </article>
          </>
        ) : null}
        <article className="kpi-card">
          <p className="kpi-label">Failed payments</p>
          <p className="kpi-value">
            {failSummaryError ? '—' : formatNumber(failCount)}
          </p>
          <p className="kpi-hint">invoice.payment_failed in range</p>
        </article>
      </div>

      {canMutate ? (
        <section className="chart-card">
          <h2>Failed payments</h2>
          <p className="chart-hint">User ID roster for support triage. Open a user to see email.</p>
          {failListError ? (
            <p className="chart-empty">Could not load failed-payment roster.</p>
          ) : failRows.length === 0 ? (
            <p className="chart-empty">No failed payments in this range.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">User ID</th>
                    <th scope="col">Attempts</th>
                    <th scope="col">Failed at</th>
                    <th scope="col">Open user</th>
                  </tr>
                </thead>
                <tbody>
                  {failRows.map((row) => (
                    <tr key={`${row.userId}-${row.failedAt}`}>
                      <td>{row.userId}</td>
                      <td>{formatNumber(row.attemptCount)}</td>
                      <td>{formatDateTime(row.failedAt)}</td>
                      <td>
                        <Link to={`/users?userId=${encodeURIComponent(row.userId)}`}>
                          Open user
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {capHit ? <p className="chart-hint">{FAIL_LIST_CAP_CAPTION}</p> : null}
        </section>
      ) : null}

      {!loading && !error && hasMrrData && (
        <div className="product-grid">
          <section className="chart-card">
            <h2>MRR trend</h2>
            <p className="chart-hint">Stripe cache snapshots over selected range</p>
            <TimeSeriesChart
              series={mrrSeries}
              field="mrrCents"
              dateField="asOf"
              mode="line"
              formatY={(value) => formatCurrency(value, mrr.currency)}
              emptyMessage="Not enough snapshot data for MRR trend chart."
              ariaLabel="MRR snapshots over the selected range"
            />
          </section>

          <section className="chart-card">
            <h2>Plan breakdown</h2>
            <p className="chart-hint">Accounts and MRR by tier</p>
            {planRows.length === 0 ? (
              <p className="chart-empty">No plan breakdown in cache snapshot.</p>
            ) : (
              <>
                <HorizontalBarChart
                  items={planRows.map((row) => ({
                    label: row.tierSlug,
                    value: row.count ?? 0,
                    detail: row.interval
                      ? row.interval.charAt(0).toUpperCase() + row.interval.slice(1)
                      : null,
                  }))}
                  emptyMessage="No plan breakdown in cache snapshot."
                />
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th scope="col">Tier</th>
                        <th scope="col">Interval</th>
                        <th scope="col">Subs</th>
                        <th scope="col">MRR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planRows.map((row) => (
                        <tr key={`${row.tierSlug}-${row.interval}`}>
                          <td>{row.tierSlug}</td>
                          <td>
                            {row.interval
                              ? row.interval.charAt(0).toUpperCase() + row.interval.slice(1)
                              : '—'}
                          </td>
                          <td>{formatNumber(row.count)}</td>
                          <td>{formatCurrency(row.mrrCents, mrr.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </ModuleShell>
  );
}
